import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Badge } from '@mdvip/shared/components/ui/badge';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import { useCurrentBalance } from '@/hooks/payments/balance/useCurrentBalance';
import { deriveBalanceState, isOverdue } from '@/lib/payments/balance.format';
import { Metric } from './Metric';

interface CurrentBalanceMetricProps {
  /** Which of PaymentSummaryCard's two responsive layouts this instance renders. Both mount
   *  simultaneously (CSS visibility, not conditional) — this only changes the markup shape. */
  layout: 'narrow' | 'wide';
  /** Positional padding for the wide 3-column grid, or the narrow column wrapper — owned by the
   *  parent grid, not this metric. */
  className?: string;
}

/**
 * Current Balance metric for the "Payment summary" card — the member's outstanding balance, due
 * date, and past-due status. Read-only, no write path exists for this data anywhere in the app.
 * Owns its own loading/error state, rendered inline within its own slot rather than gating the
 * whole card, so a slow or failed lookup here doesn't block the other metrics from painting.
 */
export function CurrentBalanceMetric({ layout, className }: CurrentBalanceMetricProps) {
  const { t } = useTranslation('members');
  const tError = useErrorTranslation();
  const { data, isLoading, isError, error, refetch } = useCurrentBalance();
  const { formatCurrency } = useCurrency();
  const { formatDateOnly } = useFormatDate();

  const amount = data?.amount ?? null;
  const state = deriveBalanceState(amount);
  const overdue = data?.dueDate ? isOverdue(data.dueDate) : false;
  const overdueDue = state === 'due' && overdue;
  const label = state === 'credit' ? t('payments.balance.creditLabel') : t('payments.balance.dueLabel');
  const colorClass = overdueDue
    ? 'text-destructive-foreground'
    : state === 'paid' || state === 'credit'
      ? 'text-success-foreground'
      : 'text-foreground';
  const amountLabel = state === 'credit' ? `(${formatCurrency(Math.abs(amount ?? 0))})` : formatCurrency(amount ?? 0);
  const dueDateText = data?.dueDate
    ? t('payments.balance.dueDate', { date: formatDateOnly(data.dueDate) })
    : t('payments.balance.dueDateUnavailable');

  // The badge always carries the day count/"Due Today" text — sourced from billingDaysPastDue
  // directly, never re-derived — in both layouts. The design's narrow layout falls back to plain
  // "Past due" text with no day count, but that loses "Due Today" (daysPastDue === 0), which this
  // story's AC requires everywhere, not just at the wide breakpoint.
  //
  // Note: dueDate and daysPastDue are independently populated fields — an overdue balance with a
  // null daysPastDue is possible. We render no badge at all in that case, just the due-date text
  // below, rather than guessing a day count or falling back to a genericized "Past due" label.
  const pastDueBadge =
    overdueDue && data?.daysPastDue != null ? (
      <Badge variant="destructive">
        <Icon name="alertTriangle" weight="solid" size="xs" />
        {t('payments.balance.daysPastDue', { count: data.daysPastDue })}
      </Badge>
    ) : null;

  const errorBlock = (
    <Alert variant="destructive">
      <Icon name="alertTriangle" weight="solid" />
      {/* The shared Alert's title defaults to a single-line clamp, sized for a full-width banner —
          override it here since this Alert renders inside a much narrower metric column. */}
      <AlertTitle className="line-clamp-none">{t('payments.balance.errorTitle')}</AlertTitle>
      <AlertDescription>
        <p>{tError(extractGqlErrorCode(error))}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
          <Icon name="refreshCw" size="sm" /> {t('payments.balance.retry')}
        </Button>
      </AlertDescription>
    </Alert>
  );

  if (layout === 'narrow') {
    if (isLoading) {
      return (
        <div className={className} aria-busy="true">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="mt-2 h-8 w-24" />
        </div>
      );
    }
    return (
      <div className={className}>
        {isError ? (
          errorBlock
        ) : (
          <>
            <div className="text-sm text-muted-foreground">{label}</div>
            <div className={cn('mt-1 text-[32px] font-bold leading-none tracking-tight tabular-nums', colorClass)}>
              {state === 'unavailable' ? '—' : amountLabel}
            </div>
            {state === 'unavailable' && (
              <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                <Icon name="info" size="sm" />
                {t('payments.balance.unavailable')}
              </div>
            )}
            {state === 'paid' && (
              <div className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-success-foreground">
                <Icon name="circleCheck" weight="solid" size="sm" />
                {t('payments.balance.paidUp')}
              </div>
            )}
            {state === 'credit' && (
              <div className="mt-2 text-sm font-semibold text-success-foreground">
                {t('payments.balance.creditBalance')}
              </div>
            )}
            {state === 'due' && !overdueDue && <div className="mt-2 text-sm text-muted-foreground">{dueDateText}</div>}
            {overdueDue && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {pastDueBadge}
                <span className="text-muted-foreground">{dueDateText}</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <Metric
        label={<Skeleton className="h-3.5 w-24" />}
        className={className}
        data-slot="metric-current-balance"
        data-state="loading"
        aria-busy="true"
      >
        <Skeleton className="h-8 w-24" />
        <Skeleton className="mt-2 h-3 w-20" />
      </Metric>
    );
  }

  return (
    <Metric
      label={label}
      className={className}
      data-slot="metric-current-balance"
      data-state={isError ? 'error' : state}
    >
      {isError ? (
        errorBlock
      ) : (
        <>
          <div className={cn('text-3xl font-semibold leading-none tabular-nums', colorClass)}>
            {state === 'unavailable' ? '—' : amountLabel}
          </div>
          {state === 'unavailable' && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon name="info" size="xs" />
              {t('payments.balance.unavailable')}
            </div>
          )}
          {state === 'paid' && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-success-foreground">
              <Icon name="circleCheck" weight="solid" size="xs" />
              {t('payments.balance.paidUp')}
            </div>
          )}
          {state === 'credit' && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="success">
                <Icon name="piggyBank" weight="solid" size="xs" />
                {t('payments.balance.creditBadge')}
              </Badge>
              <span className="text-muted-foreground">{t('payments.balance.creditCaption')}</span>
            </div>
          )}
          {state === 'due' && !overdueDue && <div className="mt-2 text-xs text-muted-foreground">{dueDateText}</div>}
          {overdueDue && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {pastDueBadge}
              <span className="text-muted-foreground">{dueDateText}</span>
            </div>
          )}
        </>
      )}
    </Metric>
  );
}
